
import React, { useEffect, useState, useMemo } from "react";
import { collection, addDoc, query, orderBy, onSnapshot, doc, updateDoc, deleteDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { useEmployeeAuth } from "../auth/EmployeeAuthContext";
import { motion, AnimatePresence } from "framer-motion";
import { FaPlus, FaSearch, FaDownload, FaTrash, FaUserEdit, FaEye, FaFire } from "react-icons/fa";
import Papa from "papaparse";

const HrManageEmployees = () => {
  const { user: hr } = useEmployeeAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [queryText, setQueryText] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [selected, setSelected] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null);

  const emptyForm = {
    name: "",
    email: "",
    employeeId: "",
    department: "",
    designation: "",
    role: "employee",
    status: "active",
    profilePic: "",
    password: ""  // ← YE ADD KIYA
  };

  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    const q = query(collection(db, "employees"), orderBy("employeeId"));
    const unsub = onSnapshot(q, (snap) => {
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setEmployees(docs);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const filtered = useMemo(() => {
    return employees.filter(e => {
      const q = queryText.toLowerCase();
      const matches = !q || 
        e.name?.toLowerCase().includes(q) || 
        e.email?.toLowerCase().includes(q) || 
        e.employeeId?.toLowerCase().includes(q);
      const roleOk = !filterRole || e.role === filterRole;
      const statusOk = !filterStatus || e.status === filterStatus;
      return matches && roleOk && statusOk;
    });
  }, [employees, queryText, filterRole, filterStatus]);

  const toggleSelect = (id) => {
    setSelected(s => ({ ...s, [id]: !s[id] }));
  };

  const bulkAction = async (action) => {
    const ids = Object.keys(selected).filter(k => selected[k]);
    if (!ids.length) return alert("Select employees first");
    if (!window.confirm(`${action} ${ids.length} employees?`)) return;
    try {
      await Promise.all(ids.map(id => 
        action === "deactivate" 
          ? updateDoc(doc(db, "employees", id), { status: "inactive", updatedAt: serverTimestamp() })
          : deleteDoc(doc(db, "employees", id))
      ));
      setSelected({});
    } catch (e) { alert("Failed"); }
  };

  const openAdd = () => { setEditing(null); setForm(emptyForm); setShowModal(true); };
  const openEdit = (emp) => {
    setEditing(emp);
    setForm({ ...emp });
    setShowModal(true);
  };


  const handleSave = async (e) => {
    e.preventDefault();

    const email = form.email.trim().toLowerCase();
    if (!email || !email.includes("@")) {
      return alert("Valid email is required");
    }

    try {
      if (editing) {
        // EDIT: Replace document at NEW email ID (but keep all old data via merge)
        const newDocRef = doc(db, "employees", email);

        await setDoc(newDocRef, {
          ...form,
          email, // ensure email field is saved
          updatedAt: serverTimestamp(),
        }, { merge: true }); // YE LINE SABSE ZAROORI HAI

        // Agar email change hua hai → purana document delete kar do
        if (editing.id !== email) {
          await deleteDoc(doc(db, "employees", editing.id));
        }
      } else {
        // ADD: Create new with email as ID
        await setDoc(doc(db, "employees", email), {
          ...form,
          email,
          password: form.password || "DRVEMP#1605",
          createdBy: hr?.email,
          joinDate: serverTimestamp(),
          createdAt: serverTimestamp(),
          status: "active",
          role: form.role || "employee",
        });
      }

      setShowModal(false);
      setForm(emptyForm);
    } catch (e) {
      console.error(e);
      alert("Failed to save. Email might already exist or permission issue.");
    }
  };

  const handleExport = () => {
    const csv = Papa.unparse(filtered.map(e => ({
      employeeId: e.employeeId, name: e.name, email: e.email,
      department: e.department, designation: e.designation,
      role: e.role, status: e.status
    })));
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `employees_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
  };

  return (
    <div className="min-h-screen overflow-hidden bg-gradient-to-br from-neutral-950 via-purple-950 to-indigo-950">

      {/* HERO */}
      <div className="pt-16 pb-6 px-8 text-center">
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-7xl font-black bg-gradient-to-r from-white via-white/90 to-white/70 bg-clip-text text-transparent"
        >
          Manage Employees
        </motion.h1>
        <p className="text-white/60 text-xl mt-4">Add, edit, deactivate — all in real-time</p>
      </div>

      {/* FILTER BAR */}
      <div className="max-w-7xl mx-auto px-8 mb-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="backdrop-blur-3xl bg-white/10 border border-white/20 rounded-3xl p-6 flex flex-wrap gap-4 items-center justify-between"
        >
          <div className="flex items-center gap-4 flex-1">
            <div className="relative">
              <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50" />
              <input
                value={queryText}
                onChange={e => setQueryText(e.target.value)}
                placeholder="Search name, email, ID..."
                className="pl-12 pr-6 py-4 bg-white/10 border border-white/20 rounded-2xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-cyan-400 w-80"
              />
            </div>

            <select value={filterRole} onChange={e => setFilterRole(e.target.value)} className="px-6 py-4 bg-white/10 border border-white/20 rounded-2xl text-white">
              <option value="">All Roles</option>
              <option value="employee">Employee</option>
              <option value="hr">HR</option>
              <option value="admin">Admin</option>
            </select>

            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="px-6 py-4 bg-white/10 border border-white/20 rounded-2xl text-white">
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="flex gap-4">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleExport}
              className="px-6 py-4 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl text-white font-bold shadow-xl hover:shadow-emerald-500/50 flex items-center gap-3"
            >
              <FaDownload /> Export
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={openAdd}
              className="px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl text-white font-bold shadow-xl hover:shadow-cyan-500/50 flex items-center gap-3"
            >
              <FaPlus /> Add Employee
            </motion.button>
          </div>
        </motion.div>
      </div>

      {/* TABLE */}
      <div className="max-w-7xl mx-auto px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="backdrop-blur-3xl bg-white/10 border border-white/20 rounded-3xl overflow-hidden"
        >
          <div className="p-6 border-b border-white/10 flex justify-between items-center">
            <p className="text-white/80 text-lg">{filtered.length} employees</p>
            <div className="flex gap-3">
              <button onClick={() => bulkAction("deactivate")} className="px-5 py-3 bg-orange-500/20 hover:bg-orange-500/40 rounded-2xl text-orange-300 font-medium transition">
                Deactivate Selected
              </button>
              <button onClick={() => bulkAction("delete")} className="px-5 py-3 bg-red-500/20 hover:bg-red-500/40 rounded-2xl text-red-300 font-medium transition flex items-center gap-2">
                <FaTrash /> Delete
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white">
                  <th className="p-5 text-left">
                    <input type="checkbox" onChange={e => {
                      const checked = e.target.checked;
                      const map = {};
                      filtered.forEach(emp => map[emp.id] = checked);
                      setSelected(map);
                    }} className="w-5 h-5 rounded bg-white/20" />
                  </th>
                  <th className="p-5 text-left">ID</th>
                  <th className="p-5 text-left">Employee</th>
                  <th className="p-5 text-left">Email</th>
                  <th className="p-5 text-left">Dept</th>
                  <th className="p-5 text-left">Role</th>
                  <th className="p-5 text-left">Status</th>
                  <th className="p-5 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="p-20 text-center text-white/50">Loading employees...</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={8} className="p-20 text-center text-white/50">No employees found</td></tr>
                ) : filtered.map((emp, i) => (
                  <motion.tr
                    key={emp.id}
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className="border-b border-white/10 hover:bg-white/5 transition"
                  >
                    <td className="p-5">
                      <input checked={!!selected[emp.id]} onChange={() => toggleSelect(emp.id)} type="checkbox" className="w-5 h-5 rounded bg-white/20" />
                    </td>
                    <td className="p-5 text-cyan-300 font-bold">{emp.employeeId}</td>
                    <td className="p-5">
                      <div className="flex items-center gap-4">
                        <img src={emp.profilePic || `https://ui-avatars.com/api/?name=${emp.name}&background=0f172a&color=06b6d4`} alt="" className="w-12 h-12 rounded-full ring-2 ring-cyan-400/50" />
                        <div>
                          <p className="text-white font-semibold">{emp.name}</p>
                          <p className="text-white/60 text-sm">{emp.designation || "—"}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-5 text-white/80">{emp.email}</td>
                    <td className="p-5 text-purple-300">{emp.department || "—"}</td>
                    <td className="p-5">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                        emp.role === "admin" ? "bg-pink-500/30 text-pink-300" :
                        emp.role === "hr" ? "bg-orange-500/30 text-orange-300" :
                        "bg-cyan-500/30 text-cyan-300"
                      }`}>
                        {emp.role}
                      </span>
                    </td>
                    <td className="p-5">
                      <button
                        onClick={async () => {
                          await updateDoc(doc(db, "employees", emp.id), {
                            status: emp.status === "active" ? "inactive" : "active",
                            updatedAt: serverTimestamp()
                          });
                        }}
                        className={`px-4 py-2 rounded-full text-xs font-bold transition ${
                          emp.status === "active"
                            ? "bg-emerald-500/30 text-emerald-300 hover:bg-emerald-500/50"
                            : "bg-gray-500/30 text-gray-300 hover:bg-gray-500/50"
                        }`}
                      >
                        {emp.status}
                      </button>
                    </td>
                    <td className="p-5">
                      <div className="flex gap-3">
                        <button onClick={() => setPreview(emp)} className="p-3 bg-white/10 hover:bg-white/20 rounded-xl transition">
                          <FaEye className="text-cyan-300" />
                        </button>
                        <button onClick={() => openEdit(emp)} className="p-3 bg-cyan-500/20 hover:bg-cyan-500/40 rounded-xl transition">
                          <FaUserEdit className="text-cyan-300" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>

      {/* ADD / EDIT MODAL */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-xl z-50 flex items-center justify-center p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              className="bg-white/10 backdrop-blur-3xl border border-white/20 rounded-3xl p-10 w-full max-w-3xl"
              onClick={e => e.stopPropagation()}
            >
              <h2 className="text-3xl font-bold text-white mb-6 text-center">
                {editing ? "Edit Employee" : "Add New Employee"}
              </h2>
              <form onSubmit={handleSave} className="grid grid-cols-2 gap-6">
                {["name", "email", "employeeId", "department", "designation"].map((field) => (
                  <input
                    key={field}
                    required
                    type={field === "email" ? "email" : "text"}
                    placeholder={field.charAt(0).toUpperCase() + field.slice(1)}
                    value={form[field]}
                    onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
                    className="px-6 py-4 bg-white/10 border border-white/20 rounded-2xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-cyan-400"
                  />
                ))}

                {/* ADD THIS: Password Field */}
                {!editing && (
                  <input
                    required
                    type="text"
                    placeholder="Initial Password (they can change later)"
                    value={form.password || ""}
                    onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                    className="px-6 py-4 bg-white/10 border border-white/20 rounded-2xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-cyan-400 col-span-2"
                  />
                )}

                <select
                  value={form.role}
                  onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                  className="px-6 py-4 bg-white/10 border border-white/20 rounded-2xl text-white"
                >
                  <option value="employee">Employee</option>
                  <option value="hr">HR</option>
                  <option value="admin">Admin</option>
                </select>
                <select
                  value={form.status}
                  onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                  className="px-6 py-4 bg-white/10 border border-white/20 rounded-2xl text-white"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
                <div className="col-span-2 flex justify-center gap-6 mt-6">
                  <button type="button" onClick={() => setShowModal(false)} className="px-10 py-4 bg-white/10 hover:bg-white/20 rounded-2xl text-white font-bold transition">
                    Cancel
                  </button>
                  <button type="submit" className="px-12 py-4 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl text-white font-bold shadow-xl hover:shadow-cyan-500/50 transition">
                    {editing ? "Save Changes" : "Create Employee"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* PREVIEW MODAL */}
      <AnimatePresence>
        {preview && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-xl z-50 flex items-center justify-center p-4"
            onClick={() => setPreview(null)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              className="bg-white/10 backdrop-blur-3xl border border-white/20 rounded-3xl p-10 w-full max-w-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center gap-6 mb-6">
                <img src={preview.profilePic || `https://ui-avatars.com/api/?name=${preview.name}`} alt="" className="w-24 h-24 rounded-full ring-4 ring-cyan-400/50" />
                <div>
                  <h3 className="text-3xl font-bold text-white">{preview.name}</h3>
                  <p className="text-xl text-cyan-300">{preview.designation}</p>
                  <p className="text-purple-300">{preview.department}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-6 text-white">
                <div className="bg-white/5 rounded-2xl p-5">
                  <p className="text-white/60">Employee ID</p>
                  <p className="text-2xl font-bold text-cyan-300">{preview.employeeId}</p>
                </div>
                <div className="bg-white/5 rounded-2xl p-5">
                  <p className="text-white/60">Email</p>
                  <p className="text-lg">{preview.email}</p>
                </div>
                <div className="bg-white/5 rounded-2xl p-5">
                  <p className="text-white/60">Role</p>
                  <p className="text-xl font-bold text-pink-300">{preview.role}</p>
                </div>
                <div className="bg-white/5 rounded-2xl p-5">
                  <p className="text-white/60">Status</p>
                  <p className={`text-xl font-bold ${preview.status === "active" ? "text-emerald-300" : "text-gray-400"}`}>
                    {preview.status}
                  </p>
                </div>
              </div>
              <div className="mt-8 text-center">
                <button
                  onClick={() => { openEdit(preview); setPreview(null); }}
                  className="px-10 py-4 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl text-white font-bold shadow-xl hover:shadow-cyan-500/50 transition"
                >
                  Edit Profile
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* FOOTER */}
      <div className="text-center mt-6 text-white/40 text-sm">
        <p>
          Powered by <strong className="text-cyan-300">DriveEasy HR</strong> • 
          Real-time sync • End-to-end encrypted
        </p>
      </div>
    </div>
  );
};

export default HrManageEmployees;
